/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState } from 'react'
import styled from '@emotion/styled'
import {
  ResponsiveChartContainer,
  LinePlot,
  MarkPlot,
  ChartsLegend,
  ChartsTooltip,
  ChartsReferenceLine,
  ChartsXAxis,
  ChartsYAxis,
  LegendRendererProps,
  LineSeriesType,
  axisClasses,
  markElementClasses,
} from '@mui/x-charts'
import { TriggerOptions } from '@mui/x-charts/ChartsTooltip/utils'
import { analytics } from 'Modules/Analytics'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import { useAppState } from 'Context/AppContext/AppContext'
import { formatLargeNumber } from '../utils'
import { GoalGroupChartType } from './types'
import { CustomTooltip } from './CustomTooltip'

const CHART_HEIGHT = 600

type FunderChartProps = {
  chartType: GoalGroupChartType
  shouldShowLegend: boolean
  series: LineSeriesType[]
  target: number
  unitOfMeasurement: string
  xLabels: string[]
}

const Chart = styled(ResponsiveChartContainer)`
  @media print {
    /* Set a fixed height instead of 100% so that space for the svg is reserved during print render */
    & svg {
      height: ${CHART_HEIGHT}px;
    }
  }
`

export const FunderChart = ({
  chartType,
  shouldShowLegend,
  series,
  target,
  unitOfMeasurement,
  xLabels,
}: FunderChartProps) => {
  const {
    userConfig: { id, email },
  } = useAppState()
  const [tooltipTrigger, setTooltipTrigger] = useState<TriggerOptions>('none')

  const legendSlotProps: Partial<LegendRendererProps> = {
    itemMarkWidth: 20,
    itemMarkHeight: 20,
    position: { vertical: 'middle', horizontal: 'right' },
    padding: 5,
    labelStyle: {
      fontSize: '16px',
      textOverflow: 'ellipsis',
      maxWidth: '200px',
    },
  }

  const maxDataPoint = Math.max(
    0,
    ...series.flatMap(
      (serie) =>
        serie?.data?.filter((value): value is number => value != null) ?? []
    )
  )

  const calculateYAxisMax = () => {
    if (chartType === GoalGroupChartType.taskCompletion) {
      // For task completion, round up to the nearest 10
      return Math.ceil(maxDataPoint / 10) * 10
    }
    return Math.max(target, maxDataPoint) * 1.05
  }

  const yAxisMax = calculateYAxisMax()

  const yAxisLabel =
    chartType === GoalGroupChartType.taskCompletion
      ? 'Tasks Completed'
      : unitOfMeasurement

  const rightMargin = shouldShowLegend ? 300 : 80

  const dispatchDataPointHoverEvent = () => {
    analytics.trackActionCreator(
      funderGoals.Funder_Goal_Progress_Chart_Data_Point_Hovered
    )({
      userId: id,
      userEmail: email,
      chartType,
    })
  }

  const handleDataPointHover = () => {
    dispatchDataPointHoverEvent()
    setTooltipTrigger('item')
  }

  return (
    <Chart
      height={CHART_HEIGHT}
      series={series as LineSeriesType[]}
      xAxis={[{ data: xLabels, scaleType: 'point' }]}
      yAxis={[{ min: 0, max: yAxisMax }]}
      margin={{ left: 100, right: rightMargin, bottom: 80, top: 20 }}
      sx={{
        [`& .${axisClasses.left} .${axisClasses.label}`]: {
          transform: 'translateX(-30px)',
        },
        [`& .${axisClasses.bottom} .${axisClasses.label}`]: {
          transform: 'translateY(20px)',
        },
        [`& .${markElementClasses.root}`]: {
          scale: '0.8',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '&:hover': {
            scale: '0.8',
            opacity: 1,
          },
        },
      }}
    >
      <ChartsXAxis
        label='Date'
        labelStyle={{
          fontWeight: 'bold',
          fontSize: 16,
        }}
        tickLabelStyle={{
          angle: 0,
          textAnchor: 'middle',
          fontSize: 12,
        }}
        position='bottom'
      />
      <ChartsTooltip
        trigger={tooltipTrigger}
        slots={{
          itemContent: CustomTooltip,
        }}
      />
      <ChartsYAxis
        label={yAxisLabel}
        labelStyle={{
          fontWeight: 'bold',
          fontSize: 16,
        }}
        tickSize={10}
        position='left'
        tickLabelStyle={{
          fontSize: 12,
        }}
        valueFormatter={(value: number) => formatLargeNumber(value)}
      />
      <LinePlot />
      <MarkPlot onMouseEnter={() => handleDataPointHover()} />
      <ChartsLegend
        direction='column'
        position={{ vertical: 'middle', horizontal: 'right' }}
        slotProps={{
          legend: legendSlotProps,
        }}
        hidden={!shouldShowLegend}
      />
      {target && chartType === GoalGroupChartType.goalProgress && (
        <ChartsReferenceLine
          y={target}
          label='Target'
          labelAlign='start'
          lineStyle={{
            stroke: 'black',
            strokeWidth: 2,
            strokeDasharray: '5 5',
          }}
        />
      )}
    </Chart>
  )
}
